import { deleteFile, downloadFile, downloadSharedFile, listMyFiles, listMyUploads, listSharedFiles, uploadFile, listSharesForFile, getFileMetadata, getSharedFileMetadata, shareFile, unshareFile, abortUpload, abortDownload } from "@/services/files.service";
import { formatFileSize, formatDate, determineFileType, determineFileColor, determineFileIcon } from '@/utils/files'
import { getUploadErrorMessage, getDownloadErrorMessage, getShareErrorMessage, getFileOperationErrorMessage } from "@/services/error.service";
import { cryptoService } from "@/services/crypto.service";
import { redirectToUnlock } from '@/utils/redirectToUnlock';
import { enableNavigationPrevention, disableNavigationPrevention, isNavigationPreventionActive } from '@/utils/navigationPrevention';

const state = {
    files: [],
    uploads: [],
    sharedFiles: [],
    isLoading: {
        myFiles: false,
        sharedFiles: false,
        myUploads: false,
        upload: false,
        shares: false,
        download: false
    },
    error: null,
    totalCount: 0,
    currentFile: null,
    isLoadingFile: false,
    fileError: null,
    fileShares: {},
    shareLoadingStates: {},
    activeOperations: {
        uploads: new Map(),
        downloads: new Map()
    },
    operationCounters: {
        upload: 0,
        download: 0
    },
    uploadQueue: {
        pending: [],
        processing: new Set(),
        maxConcurrent: 1 
    }
};

const mutations = {
    SET_MY_FILES(state, files) {
        state.files = files;
    },
    SET_MY_UPLOADS(state, uploads) {
        state.uploads = uploads;
    },
    SET_SHARED_FILES(state, sharedFiles) {
        state.sharedFiles = sharedFiles;
    },
    UPLOAD_FILE(state, file) {
        state.files.push(file);
    },
    DELETE_FILE(state, fileId) {
        state.files = state.files.filter(file => file.id !== fileId);
    },
    SET_LOADING(state, { screen, isLoading }) {
        state.isLoading[screen] = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_TOTAL_COUNT(state, count) {
        state.totalCount = count;
    },
    SET_CURRENT_FILE(state, file) {
        state.currentFile = file;
    },
    SET_LOADING_FILE(state, isLoading) {
        state.isLoadingFile = isLoading;
    },
    SET_FILE_ERROR(state, error) {
        state.fileError = error;
    },
    SET_FILE_SHARES(state, { fileId, shares }) {
        state.fileShares = {
            ...state.fileShares,
            [fileId]: shares
        };
    },
    SET_SHARE_LOADING(state, { fileId, isLoading }) {
        state.shareLoadingStates = {
            ...state.shareLoadingStates,
            [fileId]: isLoading
        };
    },
    // New mutations for abort functionality
    ADD_ACTIVE_OPERATION(state, { operation, operationId, data }) {
        state.activeOperations[`${operation}s`].set(operationId, data);
        // Increment counter for next operation
        state.operationCounters[operation]++;
    },
    REMOVE_ACTIVE_OPERATION(state, { operation, operationId }) {
        state.activeOperations[`${operation}s`].delete(operationId);
    },
    CLEAR_ALL_OPERATIONS(state, operation) {
        state.activeOperations[`${operation}s`].clear();
    },
    INCREMENT_OPERATION_COUNTER(state, operation) {
        state.operationCounters[operation]++;
    },
    // Upload queue mutations
    ADD_TO_UPLOAD_QUEUE(state, queueItem) {
        state.uploadQueue.pending.push(queueItem);
    },
    REMOVE_FROM_UPLOAD_QUEUE(state, operationId) {
        state.uploadQueue.pending = state.uploadQueue.pending.filter(item => item.operationId !== operationId);
    },
    ADD_TO_PROCESSING(state, operationId) {
        state.uploadQueue.processing.add(operationId);
    },
    REMOVE_FROM_PROCESSING(state, operationId) {
        state.uploadQueue.processing.delete(operationId);
    },
    CLEAR_UPLOAD_QUEUE(state) {
        state.uploadQueue.pending = [];
        state.uploadQueue.processing.clear();
    },
    UPDATE_QUEUE_STATUS() {
        // This mutation will be used to trigger reactivity for queue status updates
        // The actual status updates are handled in actions
    },
    SET_UPLOAD_QUEUE_MAX_CONCURRENT(state, maxConcurrent) {
        state.uploadQueue.maxConcurrent = maxConcurrent;
    }
};

const actions = {

    async fetchMyFilesAction({ commit, dispatch, state }) {
        // Prevent duplicate fetches if already loading
        if (state.isLoading.myFiles) {
            console.log('[Files] Already loading files, skipping duplicate request');
            return;
        }
        
        commit("SET_LOADING", { screen: "myFiles", isLoading: true });
        commit("SET_ERROR", null);
        try {
            const response = await listMyFiles();
            if (response.files && Array.isArray(response.files)) {
                const mappedFiles = response.files.map(file => ({
                    id: file.fileId,
                    contentType: file.contentType,
                    fileType: determineFileType(file.name),
                    icon: determineFileIcon(determineFileType(file.name)),
                    color: determineFileColor(determineFileType(file.name)),
                    title: file.name,
                    isEncrypted: file.isEncrypted,
                    size: formatFileSize(file.size),
                    datetime: formatDate(file.uploadedAt),
                    ownerId: file.ownerId
                }));
                commit("SET_MY_FILES", mappedFiles);
                commit("SET_TOTAL_COUNT", response.count || 0);
                dispatch("analytics/trackEvent", {
                    event: 'files_loaded',
                    properties: {
                        count: mappedFiles.length
                    }
                }, { root: true });
            } else {
                commit("SET_MY_FILES", []);
                commit("SET_TOTAL_COUNT", 0);
                dispatch("snackbar/showSnackbar", { title: "No files found", color: "warning" }, { root: true });
            }
        } catch (error) {
            commit("SET_ERROR", error.message);
            dispatch("snackbar/showSnackbar", { title: "Error loading files!", color: "danger" }, { root: true });
            dispatch("analytics/trackEvent", {
                event: 'files_load_failed',
                properties: {
                    error: error.message
                }
            }, { root: true });
        } finally {
            commit("SET_LOADING", { screen: "myFiles", isLoading: false });
        }
    },
    
    async fetchMyUploadsAction({ commit, dispatch }) {
        commit("SET_LOADING", { screen: "myUploads", isLoading: true });
        commit("SET_ERROR", null);
        try {
            const response = await listMyUploads();
            if (response.uploads && Array.isArray(response.uploads)) {
                const mappedUploads = response.uploads.map(upload => ({
                    id: upload.uploadId,
                    contentType: upload.contentType,
                    fileType: determineFileType(upload.fileName),
                    icon: determineFileIcon(determineFileType(upload.fileName)),
                    color: determineFileColor(determineFileType(upload.fileName)),
                    title: upload.fileName,
                    isEncrypted: upload.isEncrypted,
                    size: formatFileSize(upload.size),
                    datetime: formatDate(upload.createdAt),
                    ownerId: upload.userId,
                    status: upload.status,
                    parts: upload.parts,
                    completedParts: upload.completedParts,
                    encryptionInfo: upload.encryptionInfo,
                    objectKey: upload.objectKey
                }));
                commit("SET_MY_UPLOADS", mappedUploads);
                commit("SET_TOTAL_COUNT", response.count || 0);
                dispatch("analytics/trackEvent", {
                    event: 'uploads_loaded',
                    properties: {
                        count: mappedUploads.length
                    }
                }, { root: true });
            } else {
                commit("SET_MY_UPLOADS", []);
                commit("SET_TOTAL_COUNT", 0);
                dispatch("snackbar/showSnackbar", { title: "No uploads found", color: "warning" }, { root: true });
            }
        } catch (error) {
            commit("SET_ERROR", error.message);
            dispatch("snackbar/showSnackbar", { title: "Error loading uploads!", color: "danger" }, { root: true });
            dispatch("analytics/trackEvent", {
                event: 'uploads_load_failed',
                properties: {
                    error: error.message
                }
            }, { root: true });
        } finally {
            commit("SET_LOADING", { screen: "myUploads", isLoading: false });
        }
    },

    async fetchSharedFilesAction({ commit, dispatch }) {
        commit("SET_LOADING", { screen: "sharedFiles", isLoading: true });
        commit("SET_ERROR", null);
        try {
            const response = await listSharedFiles();
            if (response.files) {
                const mappedFiles = response.files.map(file => ({
                    id: file.fileId,
                    title: file.file.name,
                    contentType: file.file.contentType,
                    fileType: determineFileType(file.file.name),
                    icon: determineFileIcon(determineFileType(file.file.name)),
                    color: determineFileColor(determineFileType(file.file.name)),
                    size: formatFileSize(file.file.size),
                    datetime: formatDate(file.file.uploadedAt),
                    owner: file.owner,
                    isEncrypted: file.file.isEncrypted
                }));

                commit("SET_SHARED_FILES", mappedFiles);
                dispatch("analytics/trackEvent", {
                    event: 'shared_files_loaded',
                    properties: {
                        count: mappedFiles.length
                    }
                }, { root: true });
            } else {
                dispatch("snackbar/showSnackbar", { title: "No shared files found", color: "warning" }, { root: true });
                commit("SET_SHARED_FILES", []);
            }
        } catch (error) {
            const errorMessage = getShareErrorMessage(error);
            dispatch("snackbar/showSnackbar", { title: "Error loading shared files!", color: "danger" }, { root: true });
            commit("SET_ERROR", errorMessage);
            dispatch("analytics/trackEvent", {
                event: 'shared_files_load_failed',
                properties: {
                    error: errorMessage
                }
            }, { root: true });
        } finally {
            commit("SET_LOADING", { screen: "sharedFiles", isLoading: false });
        }
    },

    async fetchSharesForFile({ commit, dispatch }, fileId) {
        commit("SET_SHARE_LOADING", { fileId, isLoading: true });
        try {
            const response = await listSharesForFile(fileId);
            commit("SET_FILE_SHARES", { fileId, shares: response.shares });
            return response.shares;
        } catch (error) {
            const errorMessage = getFileOperationErrorMessage(error);
            dispatch("snackbar/showSnackbar", { 
                title: errorMessage, 
                color: "danger" 
            }, { root: true });
            throw error;
        } finally {
            commit("SET_SHARE_LOADING", { fileId, isLoading: false });
        }
    },

    // Action to update queue status for all pending files
    updateQueueStatus({ commit, state, getters }) {
        const queueStats = getters.getQueueStats;
        let position = 1;
        
        // Update status for all pending files
        state.uploadQueue.pending.forEach(queueItem => {
            if (queueItem.onProgress) {
                queueItem.onProgress(0, {
                    fileName: queueItem.file.name,
                    uploaded: 0,
                    total: queueItem.file.size,
                    status: `Queued (${position} of ${queueStats.pending + queueStats.processing})`,
                    currentChunk: 0,
                    totalChunks: 0,
                    bytesUploaded: 0,
                    bytesTotal: queueItem.file.size,
                    partProgress: 0,
                    isQueued: true,
                    queuePosition: position
                });
            }
            position++;
        });
        
        // Trigger reactivity update
        commit("UPDATE_QUEUE_STATUS");
    },

    // Action to update upload queue maxConcurrent based on user stats
    updateUploadQueueLimit({ commit, rootState }) {
        const userStats = rootState.user.stats;
        if (userStats && userStats.subscription && userStats.subscription.limits && userStats.subscription.limits.maxConcurrentUploads) {
            commit("SET_UPLOAD_QUEUE_MAX_CONCURRENT", userStats.subscription.limits.maxConcurrentUploads);
            console.log(`📊 Updated upload queue maxConcurrent to ${userStats.subscription.limits.maxConcurrentUploads} based on user stats`);
        } else {
            // Fallback to default value if stats not available
            commit("SET_UPLOAD_QUEUE_MAX_CONCURRENT", 1);
            console.log('📊 Using default upload queue maxConcurrent of 1 (user stats not available)');
        }
    },

    // New action to process upload queue
    async processUploadQueue({ commit, state, dispatch, rootState }) {
        // Check if we can process more uploads
        if (state.uploadQueue.processing.size >= state.uploadQueue.maxConcurrent) {
            return; // Already at max concurrent uploads
        }

        // Find the next pending upload
        const nextUpload = state.uploadQueue.pending.shift();
        if (!nextUpload) {
            return; // No pending uploads
        }

        const { operationId, file, onProgress, resolve, reject } = nextUpload;

        // Mark this upload as processing
        commit("ADD_TO_PROCESSING", operationId);

        // Update status to show processing has started
        if (onProgress) {
            onProgress(1, {
                fileName: file.name,
                uploaded: 0,
                total: file.size,
                status: 'Preparing file for upload...',
                currentChunk: 0,
                totalChunks: 0,
                bytesUploaded: 0,
                bytesTotal: file.size,
                partProgress: 0,
                isQueued: false,
                isProcessing: true
            });
        }

        try {
            // Check if encryption is initialized
            if (!rootState.encryption.isInitialized) {
                throw new Error('Encryption not initialized. Please initialize encryption before uploading files.');
            }

            // Generate file key for encryption
            const fileKey = await cryptoService.generateFileKey();
            const encryptedFileKey = await cryptoService.encryptFileKey(fileKey, cryptoService.publicKey);

            const encryptionInfo = {
                version: cryptoService.keyId, // TODO: change to keyId
                algorithm: "AES-GCM",
                iv: "", // TODO: add iv
                encryptedFileKey: encryptedFileKey
            };

            // Upload the file with the prepared encryption info
            const response = await uploadFile({ 
                file, 
                fileKey,
                encryptionInfo, 
                onProgress,
                signal: state.activeOperations.uploads.get(operationId)?.abortController?.signal
            });

            if (response.error) {
                throw new Error(response.error);
            }

            // Update the operation with upload ID if available
            if (response.uploadId) {
                const currentData = state.activeOperations.uploads.get(operationId);
                if (currentData) {
                    currentData.uploadId = response.uploadId;
                    commit("ADD_ACTIVE_OPERATION", {
                        operation: "upload",
                        operationId,
                        data: currentData
                    });
                }
            }

            dispatch("analytics/trackEvent", { event: 'upload_success' }, { root: true });
            dispatch("snackbar/showSnackbar", { title: "File uploaded successfully!", color: "success" }, { root: true });
            
            resolve(response);
        } catch (error) {
            // Check if this was an abort error
            if (error.name === 'AbortError' || error.message === 'Upload was aborted') {
                console.log('[Files Store] Upload was aborted');
                resolve(null); // Don't show error for aborted uploads
                return;
            }
            
            const errorMessage = getUploadErrorMessage(error);
            commit("SET_ERROR", errorMessage);
            dispatch("analytics/trackEvent", {
                event: 'upload_failed',
                properties: {
                    error: errorMessage
                }
            }, { root: true });

            let displayMessage = errorMessage;
            try {
                const parsed = JSON.parse(errorMessage);
                displayMessage = parsed.error || parsed.message || errorMessage;
            } catch (e) {
                // Not JSON, leave as is
            }

            dispatch("snackbar/showSnackbar", { 
                title: displayMessage, 
                color: "danger" 
            }, { root: true });
            
            reject(error);
        } finally {
            // Remove this upload from processing
            commit("REMOVE_FROM_PROCESSING", operationId);
            
            // Update queue status for remaining files
            dispatch("updateQueueStatus");
            
            // Process next upload in queue
            dispatch("processUploadQueue");
        }
    },

    async uploadFileAction({ commit, dispatch, rootState, getters }, { file, onProgress }) {
        commit("SET_LOADING", { screen: "upload", isLoading: true });
        commit("SET_ERROR", null);
        
        // Generate unique operation ID for this upload
        const operationId = getters.generateOperationId("upload");
        commit("INCREMENT_OPERATION_COUNTER", "upload");
        
        // Create abort controller for this specific upload
        const abortController = new AbortController();
        
        // Add this operation to active operations
        commit("ADD_ACTIVE_OPERATION", {
            operation: "upload",
            operationId,
            data: {
                abortController,
                file,
                uploadId: null // Will be set when we get response
            }
        });
        
        // Enable navigation prevention for upload (only if not already enabled)
        if (!isNavigationPreventionActive()) {
            enableNavigationPrevention('You have file uploads in progress. Are you sure you want to leave?');
        }
        
        // Check if this is a large file that needs multipart upload
        const isLargeFile = file.size > 100 * 1024 * 1024; // 100MB threshold
        
        if (isLargeFile) {
            // For large files, use the queue system
            return new Promise((resolve, reject) => {
                // Show queued status immediately
                if (onProgress) {
                    const queueStats = getters.getQueueStats;
                    const queuePosition = queueStats.pending + 1; // +1 because this file will be added to queue
                    onProgress(0, {
                        fileName: file.name,
                        uploaded: 0,
                        total: file.size,
                        status: `Queued (${queuePosition} of ${queueStats.pending + queueStats.processing + 1})`,
                        currentChunk: 0,
                        totalChunks: 0,
                        bytesUploaded: 0,
                        bytesTotal: file.size,
                        partProgress: 0,
                        isQueued: true,
                        queuePosition: queuePosition
                    });
                }
                
                // Add to upload queue
                commit("ADD_TO_UPLOAD_QUEUE", {
                    operationId,
                    file,
                    onProgress,
                    resolve,
                    reject
                });
                
                // Update queue status for all pending files
                dispatch("updateQueueStatus");
                
                // Try to process the queue
                dispatch("processUploadQueue");
            }).finally(() => {
                // Remove this operation from active operations
                commit("REMOVE_ACTIVE_OPERATION", { operation: "upload", operationId });
                
                // Check if there are any remaining operations
                if (state.activeOperations.uploads.size === 0 && state.activeOperations.downloads.size === 0) {
                    commit("SET_LOADING", { screen: "upload", isLoading: false });
                    // Disable navigation prevention only if no operations are active
                    disableNavigationPrevention();
                }
            });
        } else {
            // For small files, use the original direct upload approach
            try {
                // Check if encryption is initialized AND keys are ready
                if (!rootState.encryption.isInitialized || !cryptoService.areKeysReady()) {
                    throw new Error('Encryption not initialized. Please initialize encryption before uploading files.');
                }

                // Generate file key for encryption
                const fileKey = await cryptoService.generateFileKey();
                const encryptedFileKey = await cryptoService.encryptFileKey(fileKey, cryptoService.publicKey);

                // Fallback to cryptoService.keyId if keyVersion is not set
                const version = rootState.encryption.keyVersion || cryptoService.keyId;
                if (!version) {
                    throw new Error('Encryption version not available. Please try refreshing the page.');
                }
                
                const encryptionInfo = {
                    version: version,
                    algorithm: "AES-GCM",
                    iv: "", // TODO: add iv
                    encryptedFileKey: encryptedFileKey
                };
                
                // Upload the file with the prepared encryption info
                const response = await uploadFile({ 
                    file, 
                    fileKey,
                    encryptionInfo, 
                    onProgress,
                    signal: abortController.signal // Pass abort signal
                });

                if (response.error) {
                    throw new Error(response.error);
                }

                // Update the operation with upload ID if available
                if (response.uploadId) {
                    const currentData = state.activeOperations.uploads.get(operationId);
                    if (currentData) {
                        currentData.uploadId = response.uploadId;
                        commit("ADD_ACTIVE_OPERATION", {
                            operation: "upload",
                            operationId,
                            data: currentData
                        });
                    }
                }

                dispatch("analytics/trackEvent", { event: 'upload_success', }, { root: true });
                dispatch("snackbar/showSnackbar", {  title: "File uploaded successfully!", color: "success" }, { root: true });
            } catch (error) {
                // Check if this was an abort error
                if (error.name === 'AbortError' || error.message === 'Upload was aborted') {
                    console.log('[Files Store] Upload was aborted');
                    return; // Don't show error for aborted uploads
                }
                
                const errorMessage = getUploadErrorMessage(error);
                commit("SET_ERROR", errorMessage);
                dispatch("analytics/trackEvent", {
                    event: 'upload_failed',
                    properties: {
                        error: errorMessage
                    }
                }, { root: true });

                let displayMessage = errorMessage;
                try {
                    const parsed = JSON.parse(errorMessage);
                    displayMessage = parsed.error || parsed.message || errorMessage;
                } catch (e) {
                    // Not JSON, leave as is
                }

                dispatch("snackbar/showSnackbar", { 
                    title: displayMessage, 
                    color: "danger" 
                }, { root: true });
                throw error;
            } finally {
                // Remove this operation from active operations
                commit("REMOVE_ACTIVE_OPERATION", { operation: "upload", operationId });
                
                // Check if there are any remaining operations
                if (state.activeOperations.uploads.size === 0 && state.activeOperations.downloads.size === 0) {
                    commit("SET_LOADING", { screen: "upload", isLoading: false });
                    // Disable navigation prevention only if no operations are active
                    disableNavigationPrevention();
                }
            }
        }
    },

    async deleteFileAction({ commit, dispatch }, fileId) {
        commit("SET_LOADING", { screen: "myFiles", isLoading: true });
        commit("SET_ERROR", null);
        try {
            await deleteFile(fileId);
            commit("DELETE_FILE", fileId);
            dispatch("analytics/trackEvent", {
                event: 'delete_success',
                properties: {
                    fileId: fileId
                }
            }, { root: true });
            dispatch("snackbar/showSnackbar", { title: "File deleted successfully!", color: "success" }, { root: true });
        } catch (error) {
            commit("SET_ERROR", error.message);
            dispatch("analytics/trackEvent", {
                event: 'delete_failed',
                properties: {
                    error: error.message
                }
            }, { root: true });
            dispatch("snackbar/showSnackbar", { title: "Error deleting file!", color: "danger" }, { root: true });
        } finally {
            commit("SET_LOADING", { screen: "myFiles", isLoading: false });
        }
    },

    async downloadFileAction({ commit, dispatch, rootState, getters }, { fileId, onProgress }) {
        commit("SET_LOADING", { screen: "download", isLoading: true });
        
        // Generate unique operation ID for this download
        const operationId = getters.generateOperationId("download");
        commit("INCREMENT_OPERATION_COUNTER", "download");
        
        // Create abort controller for this specific download
        const abortController = new AbortController();
        
        // Add this operation to active operations
        commit("ADD_ACTIVE_OPERATION", {
            operation: "download",
            operationId,
            data: {
                abortController,
                fileId,
                fileName: rootState.files.currentFile?.name || `File ${fileId}`
            }
        });
        
        // Enable navigation prevention for download (only if not already enabled)
        if (!isNavigationPreventionActive()) {
            enableNavigationPrevention('You have file downloads in progress. Are you sure you want to leave?');
        }
        
        try {
            if (!cryptoService.areKeysReady()) {
                redirectToUnlock();
                throw new Error('Encryption is not unlocked. Please unlock encryption to download files.');
            }

            await downloadFile(fileId, onProgress, abortController.signal);
            
            // Track successful download
            dispatch("analytics/trackEvent", {
                event: 'file_downloaded',
                properties: {
                    fileId,
                    fileName: rootState.files.currentFile?.name,
                    fileSize: rootState.files.currentFile?.size,
                    fileType: rootState.files.currentFile?.contentType
                }
            }, { root: true });
            
            dispatch("snackbar/showSnackbar", { title: "File downloaded successfully!", color: "success" }, { root: true });
        } catch (error) {
            // Check if this was an abort error
            if (error.name === 'AbortError' || error.message === 'Download was aborted') {
                console.log('[Files Store] Download was aborted');
                return; // Don't show error for aborted downloads
            }
            
            const errorMessage = getDownloadErrorMessage(error);
            
            // Track error
            dispatch("analytics/trackEvent", {
                event: 'download_failed',
                properties: {
                    fileId,
                    error: error.message,
                    fileName: rootState.files.currentFile?.name
                }
            }, { root: true });
            
            dispatch("snackbar/showSnackbar", { 
                title: errorMessage, 
                color: "danger" 
            }, { root: true });
            throw error;
        } finally {
            // Remove this operation from active operations
            commit("REMOVE_ACTIVE_OPERATION", { operation: "download", operationId });
            
            // Check if there are any remaining operations
            if (rootState.files.activeOperations.uploads.size === 0 && rootState.files.activeOperations.downloads.size === 0) {
                commit("SET_LOADING", { screen: "download", isLoading: false });
                // Disable navigation prevention only if no operations are active
                disableNavigationPrevention();
            }
        }
    },

    async downloadSharedFileAction({ commit, dispatch, rootState, getters }, { fileId, onProgress }) {
        commit("SET_LOADING", { screen: "download", isLoading: true });
        
        // Generate unique operation ID for this download
        const operationId = getters.generateOperationId("download");
        commit("INCREMENT_OPERATION_COUNTER", "download");
        
        // Create abort controller for this specific download
        const abortController = new AbortController();
        
        // Add this operation to active operations
        commit("ADD_ACTIVE_OPERATION", {
            operation: "download",
            operationId,
            data: {
                abortController,
                fileId,
                fileName: `Shared File ${fileId}`
            }
        });
        
        // Enable navigation prevention for shared file download (only if not already enabled)
        if (!isNavigationPreventionActive()) {
            enableNavigationPrevention('You have file downloads in progress. Are you sure you want to leave?');
        }
        
        try {
            if (!cryptoService.areKeysReady()) {
                redirectToUnlock();
                throw new Error('Encryption is not unlocked. Please unlock encryption to download shared files.');
            }

            await downloadSharedFile(fileId, null, onProgress, abortController.signal); // Pass null for password since we're using private key
            dispatch("analytics/trackEvent", { event: 'download_success', properties: { fileId } }, { root: true });    
            dispatch("snackbar/showSnackbar", { title: "File downloaded successfully!", color: "success" }, { root: true });
        } catch (error) {
            // Check if this was an abort error
            if (error.name === 'AbortError' || error.message === 'Download was aborted') {
                console.log('[Files Store] Shared file download was aborted');
                return; // Don't show error for aborted downloads
            }
            
            const errorMessage = getDownloadErrorMessage(error);
            dispatch("analytics/trackEvent", { event: 'download_failed', properties: { error: error.message } }, { root: true });
            dispatch("snackbar/showSnackbar", { title: errorMessage, color: "danger" }, { root: true });
            throw error;
        } finally {
            // Remove this operation from active operations
            commit("REMOVE_ACTIVE_OPERATION", { operation: "download", operationId });
            
            // Check if there are any remaining operations
            if (rootState.files.activeOperations.uploads.size === 0 && rootState.files.activeOperations.downloads.size === 0) {
                commit("SET_LOADING", { screen: "download", isLoading: false });
                // Disable navigation prevention only if no operations are active
                disableNavigationPrevention();
            }
        }
    },

    async getFileMetadataAction({ commit, dispatch }, fileId) {
        commit("SET_LOADING_FILE", true);
        commit("SET_FILE_ERROR", null);
        try {
            const metadata = await getFileMetadata(fileId);
            commit("SET_CURRENT_FILE", metadata);
            
            // Track successful metadata fetch
            dispatch("analytics/trackEvent", {
                event: 'file_loaded',
                properties: {
                    fileId,
                    fileType: metadata.contentType,
                    fileSize: metadata.size,
                    isEncrypted: metadata.isEncrypted
                }
            }, { root: true });
            
            return metadata;
        } catch (error) {
            const errorMessage = getUploadErrorMessage(error);
            commit("SET_FILE_ERROR", errorMessage);
            
            // Track error
            dispatch("analytics/trackEvent", {
                event: 'file_load_failed',
                properties: {
                    fileId,
                    error: error.message
                }
            }, { root: true });
            
            dispatch("snackbar/showSnackbar", { 
                title: errorMessage, 
                color: "danger" 
            }, { root: true });
            throw error;
        } finally {
            commit("SET_LOADING_FILE", false);
        }
    },

    async getSharedFileMetadataAction({ commit, dispatch }, fileId) {
        commit("SET_LOADING_FILE", true);
        commit("SET_FILE_ERROR", null);
        try {
            const metadataResponse = await getSharedFileMetadata(fileId);
            console.log('[Files Store] Shared file metadata response:', metadataResponse);
            
            const metadata = metadataResponse.file;
            const owner = metadataResponse.owner;
            const encryptedFileKey = metadataResponse.encryptedFileKey;
            
            // Combine file metadata with owner information
            const combinedMetadata = {
                ...metadata,
                owner: owner,
                encryptedFileKey: encryptedFileKey
            };
            
            console.log('[Files Store] Combined metadata:', combinedMetadata);
            
            commit("SET_CURRENT_FILE", combinedMetadata);
            
            // Track successful metadata fetch
            dispatch("analytics/trackEvent", {
                event: 'shared_file_loaded',
                properties: {
                    fileId,
                    fileType: metadata.contentType,
                    fileSize: metadata.size,
                    isEncrypted: metadata.isEncrypted
                }
            }, { root: true });
            
            return combinedMetadata;
        } catch (error) {
            const errorMessage = getUploadErrorMessage(error);
            commit("SET_FILE_ERROR", errorMessage);
            
            // Track error
            dispatch("analytics/trackEvent", {
                event: 'shared_file_load_failed',
                properties: {
                    fileId,
                    error: error.message
                }
            }, { root: true });
            
            dispatch("snackbar/showSnackbar", { 
                title: errorMessage, 
                color: "danger" 
            }, { root: true });
            throw error;
        } finally {
            commit("SET_LOADING_FILE", false);
        }
    },

    async shareFilesAction({ dispatch }, { fileIds, contact, revoke }) {
        try {
            if (revoke) {
                await Promise.all(fileIds.map(fileId => 
                    unshareFile(fileId, contact.email)
                ));
                dispatch("snackbar/showSnackbar", { 
                    title: `Access revoked successfully from ${contact.name}`, 
                    color: "success" 
                }, { root: true });
                return;
            } else {
                if (!contact.publicKey) {
                    throw new Error('Contact public key is required for sharing');
                }
                const privateKey = cryptoService.privateKey;
                if (!privateKey) throw new Error('Private key not found. Please ensure encryption is initialized.');

                await Promise.all(fileIds.map(async fileId => {
                    const metadata = await getFileMetadata(fileId);
                    if (!metadata.isEncrypted) throw new Error('Cannot share unencrypted file');

                    const encryptedFileKey = metadata.encryptionInfo.encryptedFileKey;
                    if (!metadata.encryptionInfo?.encryptedFileKey) throw new Error('File encryption key not found in metadata');

                    const fileKey = await cryptoService.decryptFileKey(encryptedFileKey, privateKey);
                    const recipientEncryptedFileKey = await cryptoService.encryptFileKey(
                        fileKey,
                        contact.publicKey
                    );

                    await shareFile(fileId, {
                        email: contact.email,
                        encryptedFileKey: recipientEncryptedFileKey,
                        keyVersion: contact.keyId
                    });

                    dispatch("snackbar/showSnackbar", { 
                        title: `${metadata.name} shared successfully with ${contact.name}`, 
                        color: "success" 
                    }, { root: true });

                    return;
                
                }));
            }
            
        } catch (error) {
            const errorMessage = getFileOperationErrorMessage(error);
            
            dispatch("analytics/trackEvent", {
                event: 'files_share_failed',
                properties: {
                    error: error.message,
                    fileCount: fileIds.length,
                    recipientEmail: contact.email
                }
            }, { root: true });
            
            dispatch("snackbar/showSnackbar", { 
                title: errorMessage, 
                color: "danger" 
            }, { root: true });
            throw error;
        }
    },

    // New actions for aborting operations
    async abortUploadAction({ commit, rootState, dispatch }) {
        try {
            const activeUploads = rootState.files.activeOperations.uploads;
            const uploadCount = activeUploads.size;
            
            if (uploadCount === 0) {
                console.log('[Files Store] No active uploads to abort');
                return;
            }
            
            console.log(`[Files Store] Aborting ${uploadCount} active upload(s)`);
            
            // Abort all active uploads
            for (const [operationId, uploadData] of activeUploads) {
                const { abortController, uploadId } = uploadData;
                
                if (abortController) {
                    abortController.abort();
                    console.log(`[Files Store] Upload aborted via AbortController: ${operationId}`);
                }
                
                if (uploadId) {
                    try {
                        await abortUpload(uploadId);
                        console.log(`[Files Store] Upload aborted on server: ${uploadId}`);
                    } catch (error) {
                        console.warn(`[Files Store] Failed to abort upload on server: ${uploadId}`, error);
                    }
                }
            }
            
            // Clear all upload operations
            commit("CLEAR_ALL_OPERATIONS", "upload");
            
            // Clear upload queue
            commit("CLEAR_UPLOAD_QUEUE");
            
            commit("SET_LOADING", { screen: "upload", isLoading: false });
            
            // Check if there are any remaining operations
            if (rootState.files.activeOperations.downloads.size === 0) {
                // Disable navigation prevention only if no operations are active
                disableNavigationPrevention();
            }
            
            const message = uploadCount === 1 ? "Upload cancelled" : `${uploadCount} uploads cancelled`;
            dispatch("snackbar/showSnackbar", { 
                title: message, 
                color: "warning" 
            }, { root: true });
            
            dispatch("analytics/trackEvent", {
                event: 'upload_aborted',
                properties: { uploadCount }
            }, { root: true });
            
        } catch (error) {
            console.error('[Files Store] Error aborting uploads:', error);
            dispatch("snackbar/showSnackbar", { 
                title: "Error cancelling uploads", 
                color: "danger" 
            }, { root: true });
        }
    },

    async abortSpecificUploadAction({ commit, rootState, dispatch }, operationId) {
        try {
            console.log(`[Files Store] Aborting specific upload: ${operationId}`);
            const activeUploads = rootState.files.activeOperations.uploads;
            
            if (!activeUploads.has(operationId)) {
                console.log(`[Files Store] Upload operation ${operationId} not found in active operations`);
                return;
            }
            
            const uploadData = activeUploads.get(operationId);
            const { abortController, uploadId } = uploadData;
            
            if (abortController) {
                abortController.abort();
                console.log(`[Files Store] Upload aborted via AbortController: ${operationId}`);
            }
            
            if (uploadId) {
                try {
                    await abortUpload(uploadId);
                    console.log(`[Files Store] Upload aborted on server: ${uploadId}`);
                } catch (error) {
                    console.warn(`[Files Store] Failed to abort upload on server: ${uploadId}`, error);
                }
            }
            
            // Remove this specific operation from active operations
            commit("REMOVE_ACTIVE_OPERATION", { operation: "upload", operationId });
            
            // Remove from upload queue if it's pending
            commit("REMOVE_FROM_UPLOAD_QUEUE", operationId);
            
            // Remove from processing if it's currently being processed
            commit("REMOVE_FROM_PROCESSING", operationId);
            
            // Update queue status for remaining files
            dispatch("updateQueueStatus");
            
            // Try to process next upload in queue
            dispatch("processUploadQueue");
            
            // Check if there are any remaining operations
            if (activeUploads.size === 0 && rootState.files.activeOperations.downloads.size === 0) {
                commit("SET_LOADING", { screen: "upload", isLoading: false });
                // Disable navigation prevention only if no operations are active
                disableNavigationPrevention();
            }
            
            dispatch("snackbar/showSnackbar", { 
                title: "Upload cancelled", 
                color: "warning" 
            }, { root: true });
            
            dispatch("analytics/trackEvent", {
                event: 'upload_aborted',
                properties: { operationId }
            }, { root: true });
            
        } catch (error) {
            console.error(`[Files Store] Error aborting upload ${operationId}:`, error);
            dispatch("snackbar/showSnackbar", { 
                title: "Error cancelling upload", 
                color: "danger" 
            }, { root: true });
        }
    },

    async abortDownloadAction({ commit, rootState, dispatch }) {
        try {
            console.log('[Files Store] abortDownloadAction called');
            const activeDownloads = rootState.files.activeOperations.downloads;
            const downloadCount = activeDownloads.size;
            
            if (downloadCount === 0) {
                console.log('[Files Store] No active downloads to abort');
                return;
            }
            
            console.log(`[Files Store] Aborting ${downloadCount} active download(s)`);
            
            // Abort all active downloads
            for (const [operationId, downloadData] of activeDownloads) {
                const { abortController } = downloadData;
                
                if (abortController) {
                    console.log(`[Files Store] Calling abortController.abort() for: ${operationId}`);
                    abortController.abort();
                    console.log(`[Files Store] Download aborted via AbortController: ${operationId}`);
                }
            }
            
            console.log('[Files Store] Calling abortDownload() service function');
            await abortDownload();
            console.log('[Files Store] Download abort requested');
            
            // Clear all download operations
            commit("CLEAR_ALL_OPERATIONS", "download");
            commit("SET_LOADING", { screen: "download", isLoading: false });
            
            // Check if there are any remaining operations
            if (rootState.files.activeOperations.uploads.size === 0) {
                // Disable navigation prevention only if no operations are active
                disableNavigationPrevention();
            }
            
            const message = downloadCount === 1 ? "Download cancelled" : `${downloadCount} downloads cancelled`;
            dispatch("snackbar/showSnackbar", { 
                title: message, 
                color: "warning" 
            }, { root: true });
            
            dispatch("analytics/trackEvent", {
                event: 'download_aborted',
                properties: { downloadCount }
            }, { root: true });
            
        } catch (error) {
            console.error('[Files Store] Error aborting downloads:', error);
            dispatch("snackbar/showSnackbar", { 
                title: "Error cancelling downloads", 
                color: "danger" 
            }, { root: true });
        }
    },

    /**
     * Clears all file-related state
     * Used during logout or session end
     */
    clearFilesState({ commit, rootState }) {
        console.log('🗂️ Clearing files state...');
        
        // Clear all file data
        commit('SET_MY_FILES', []);
        commit('SET_MY_UPLOADS', []);
        commit('SET_SHARED_FILES', []);
        commit('SET_ERROR', null);
        commit('SET_TOTAL_COUNT', 0);
        commit('SET_CURRENT_FILE', null);
        commit('SET_FILE_ERROR', null);
        commit('SET_FILE_SHARES', {});
        commit('SET_SHARE_LOADING', {});
        
        // Clear all loading states
        commit('SET_LOADING', { screen: 'myFiles', isLoading: false });
        commit('SET_LOADING', { screen: 'sharedFiles', isLoading: false });
        commit('SET_LOADING', { screen: 'myUploads', isLoading: false });
        commit('SET_LOADING', { screen: 'upload', isLoading: false });
        commit('SET_LOADING', { screen: 'shares', isLoading: false });
        commit('SET_LOADING', { screen: 'download', isLoading: false });
        commit('SET_LOADING_FILE', false);
        
        // Abort any ongoing operations
        const activeUploads = rootState.files.activeOperations.uploads;
        const activeDownloads = rootState.files.activeOperations.downloads;
        
        console.log(`🗂️ Aborting ${activeUploads.size} active upload(s) and ${activeDownloads.size} active download(s)`);
        
        // Abort all uploads
        for (const [operationId, uploadData] of activeUploads) {
            if (uploadData.abortController) {
                uploadData.abortController.abort();
                console.log(`🗂️ Aborted upload: ${operationId}`);
            }
        }
        
        // Abort all downloads
        for (const [operationId, downloadData] of activeDownloads) {
            if (downloadData.abortController) {
                downloadData.abortController.abort();
                console.log(`🗂️ Aborted download: ${operationId}`);
            }
        }
        
        // Clear all operations
        commit('CLEAR_ALL_OPERATIONS', 'upload');
        commit('CLEAR_ALL_OPERATIONS', 'download');
        
        // Clear upload queue
        commit('CLEAR_UPLOAD_QUEUE');
        
        // Reset operation counters
        commit('INCREMENT_OPERATION_COUNTER', 'upload');
        commit('INCREMENT_OPERATION_COUNTER', 'download');
        
        // Disable navigation prevention when clearing state
        disableNavigationPrevention();
        
        console.log('✅ Files state cleared');
    },
};

const getters = {
    files: (state) => state.files,
    uploads: (state) => state.uploads,
    sharedFiles: (state) => state.sharedFiles,
    isLoadingMyFiles: (state) => state.isLoading.myFiles,
    isLoadingSharedFiles: (state) => state.isLoading.sharedFiles,
    isLoadingUpload: (state) => state.isLoading.upload,
    isLoadingDownload: (state) => state.isLoading.download,
    isLoadingMyUploads: (state) => state.isLoading.myUploads,
    isLoadingShares: (state) => state.isLoading.shares,
    hasFiles: (state) => state.files.length > 0,
    hasUploads: (state) => state.uploads.length > 0,
    getShareableFiles: (state) => state.files,
    isLoadingShare: (state) => state.isLoading.share,
    getFileById: (state) => (id) => state.files.find(file => file.id === id),
    fileCount: (state) => state.files.length,
    currentFile: state => state.currentFile,
    isLoadingFile: state => state.isLoadingFile,
    fileError: state => state.fileError,
    getFileShares: (state) => (fileId) => state.fileShares[fileId] || [],
    isFileOperationInProgress: (state) => state.isLoading.upload || state.isLoading.download || state.activeOperations.uploads.size > 0 || state.activeOperations.downloads.size > 0,
    // New getters for multiple operations
    generateOperationId: (state) => (operation) => `${operation}_${Date.now()}_${state.operationCounters[operation]}`,
    activeUploadsCount: (state) => state.activeOperations.uploads.size,
    activeDownloadsCount: (state) => state.activeOperations.downloads.size,
    totalActiveOperations: (state) => state.activeOperations.uploads.size + state.activeOperations.downloads.size,
    getActiveOperations: (state) => (operation) => Array.from(state.activeOperations[`${operation}s`].entries()),
    // Upload queue getters
    isFileQueued: (state) => (operationId) => state.uploadQueue.pending.some(item => item.operationId === operationId),
    isFileProcessing: (state) => (operationId) => state.uploadQueue.processing.has(operationId),
    getQueuePosition: (state) => (operationId) => {
        const index = state.uploadQueue.pending.findIndex(item => item.operationId === operationId);
        return index >= 0 ? index + 1 : null;
    },
    getQueueStats: (state) => ({
        pending: state.uploadQueue.pending.length,
        processing: state.uploadQueue.processing.size,
        maxConcurrent: state.uploadQueue.maxConcurrent
    }),
    getOperationIdByFile: (state) => (file) => {
        // Find operation ID by matching file object reference
        for (const [operationId, uploadData] of state.activeOperations.uploads) {
            if (uploadData.file === file) {
                return operationId;
            }
        }
        return null;
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
